import { useRef, useState } from "react";
import { Upload, Download, Replace, Trash2, Loader2, ChevronDown, ChevronRight, RotateCw, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadAttachment, fmtDate, fmtSize } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";

const UploadBox = ({ recordId, onDone }) => {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState("");
  const [pct, setPct] = useState(null);
  const [failed, setFailed] = useState(false);
  const go = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("label", label);
    setPct(0); setFailed(false);
    try {
      await api.post(`/records/${recordId}/attachments`, fd, { onUploadProgress: (e) => setPct(Math.round((e.loaded / (e.total || 1)) * 100)) });
      toast.success("Attachment uploaded");
      setFile(null); setLabel(""); if (ref.current) ref.current.value = "";
      onDone();
    } catch (e) {
      setFailed(!e.response);
      toast.error(errMsg(e));
    } finally { setPct(null); }
  };
  return (
    <div className="rounded-md border border-dashed border-slate-300 p-4 space-y-3 bg-slate-50/60" data-testid="attachment-upload-box">
      <div className="flex flex-col sm:flex-row gap-2">
        <input ref={ref} type="file" onChange={(e) => setFile(e.target.files[0])} data-testid="attachment-file-input"
          className="text-sm file:mr-3 file:rounded file:border-0 file:bg-slate-900 file:text-white file:px-3 file:py-1.5 file:text-xs" />
        <Input placeholder="Label (e.g. Signed letter)" value={label} onChange={(e) => setLabel(e.target.value)} className="bg-white sm:max-w-xs" data-testid="attachment-label-input" />
        <Button onClick={go} disabled={!file || pct !== null} data-testid="attachment-upload-button">
          {pct !== null ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : failed ? <RotateCw className="w-4 h-4 mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
          {failed ? "Retry upload" : "Upload"}
        </Button>
      </div>
      {pct !== null && <Progress value={pct} className="h-1.5" />}
      <p className="text-[11px] text-slate-500">PDF, images, Word, Excel, text — max 20 MB. A record is only linked to a file after the upload fully succeeds.</p>
    </div>
  );
};

const AttRow = ({ a, canEdit, onChanged, old }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const replace = async (f) => {
    const reason = window.prompt("Why is this file being replaced?", "Wrong/updated document") ?? "";
    const fd = new FormData(); fd.append("file", f); fd.append("reason", reason);
    setBusy(true);
    try { await api.post(`/attachments/${a.attachment_id}/replace`, fd); toast.success("New version uploaded"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    const reason = window.prompt("Reason for removing this attachment (kept in history):");
    if (!reason) return;
    try { await api.post(`/attachments/${a.attachment_id}/remove`, { reason }); toast.success("Attachment removed"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const dl = async () => { try { await downloadAttachment(a); } catch (e) { toast.error(e.response?.status === 410 ? "This file has been deleted" : "Download failed"); } };
  return (
    <div className={`flex items-center gap-3 py-2.5 ${old ? "pl-8 opacity-70" : ""}`} data-testid={`attachment-row-${a.attachment_id}`}>
      <FileText className="w-4 h-4 text-slate-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-800 truncate">{a.label} <span className="font-mono text-[11px] text-slate-500">v{a.file_version}</span>
          {a.state !== "current" && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">{a.state}</span>}</div>
        <div className="text-[11px] text-slate-500 truncate">{a.filename} · {fmtSize(a.size)} · {a.uploaded_by_name} · {fmtDate(a.created_at)} · <span className="font-mono">sha {a.sha256.slice(0, 10)}</span></div>
        {a.replace_reason && <div className="text-[11px] text-slate-500">Replaced because: {a.replace_reason}</div>}
        {a.removed_reason && <div className="text-[11px] text-slate-500">Removed: {a.removed_reason}</div>}
      </div>
      {!a.is_deleted && <Button size="sm" variant="ghost" onClick={dl} data-testid={`attachment-download-${a.attachment_id}`}><Download className="w-4 h-4" /></Button>}
      {canEdit && a.state === "current" && (<>
        <input ref={ref} type="file" className="hidden" onChange={(e) => e.target.files[0] && replace(e.target.files[0])} />
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => ref.current.click()} data-testid={`attachment-replace-${a.attachment_id}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Replace className="w-4 h-4" />}</Button>
        <Button size="sm" variant="ghost" onClick={remove} data-testid={`attachment-remove-${a.attachment_id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
      </>)}
    </div>
  );
};

export const AttachmentsPanel = ({ recordId, attachments, canEdit, required, onChanged }) => {
  const [open, setOpen] = useState({});
  const groups = {};
  attachments.forEach((a) => (groups[a.group_id] = [...(groups[a.group_id] || []), a]));
  const list = Object.values(groups).map((g) => [...g].sort((x, y) => y.file_version - x.file_version));
  return (
    <div className="space-y-4" data-testid="attachments-panel">
      {canEdit && <UploadBox recordId={recordId} onDone={onChanged} />}
      {list.length === 0 && <p className="text-sm text-slate-500" data-testid="attachments-empty">No attachments yet.{required && " At least one is required before submission."}</p>}
      <div className="divide-y divide-slate-100">
        {list.map((g) => {
          const head = g.find((a) => a.state === "current") || g[0];
          const older = g.filter((a) => a !== head);
          return (
            <div key={head.group_id}>
              <AttRow a={head} canEdit={canEdit} onChanged={onChanged} />
              {older.length > 0 && (
                <button className="text-xs text-slate-500 flex items-center gap-1 ml-7 mb-1 hover:text-slate-800" onClick={() => setOpen({ ...open, [head.group_id]: !open[head.group_id] })} data-testid={`attachment-versions-toggle-${head.group_id}`}>
                  {open[head.group_id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}{older.length} earlier version{older.length > 1 ? "s" : ""}
                </button>
              )}
              {open[head.group_id] && older.map((a) => <AttRow key={a.attachment_id} a={a} old canEdit={false} onChanged={onChanged} />)}
            </div>
          );
        })}
      </div>
    </div>
  );
};
